import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

interface AnalyticsData {
  revenue: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    total: number;
    trend: number; // percentage change
    errand_revenue: number;
    stall_revenue: number;
  };
  usage: {
    totalBookings: number;
    activeBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    utilizationRate: number;
  };
  errands: {
    total: number;
    active: number;
    completed: number;
    cash_on_delivery: number;
  };
  stalls: {
    total_rentals: number;
    active_rentals: number;
    completed_rentals: number;
    containers: {
      total: number;
      active: number;
      compartments: {
        total: number;
        available: number;
        occupied: number;
      };
    };
  };
  vendors: {
    total: number;
    approved: number;
    pending: number;
  };
  users: {
    total: number;
    newThisMonth: number;
    activeUsers: number;
    growthRate: number;
  };
  locations: {
    total: number;
    activeLocations: number;
    totalLockers: number;
    availableLockers: number;
  };
  performance: {
    averageBookingDuration: number;
    peakHours: string[];
    topLocations: Array<{ location: string; bookings: number }>;
    customerRetentionRate: number;
  };
  trends: {
    revenueGrowth: number;
    userGrowth: number;
    bookingGrowth: number;
    period: string;
  };
  pwa?: {
    total_installs: number;
    stashit_installs: number;
    dash_installs: number;
    installs_this_period: number;
    installs_by_platform: Record<string, number>;
  };
  recentActivity: Array<{
    id: string;
    type: 'booking' | 'user' | 'payment';
    description: string;
    amount?: number;
    timestamp: string;
  }>;
}

const AdminAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month' | 'year'>('month');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAnalytics();
    }
  }, [user, selectedPeriod]);

            const fetchAnalytics = async () => {
            try {
              const token = localStorage.getItem('token');
              if (!token) {
                toast.error('Authentication required');
                return;
              }

              const response = await fetch(`${API_ENDPOINTS.BASE}/api/analytics/dashboard?period=${selectedPeriod}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (response.ok) {
                const data = await response.json();
                // Map the API response to our AnalyticsData interface
                const mappedData: AnalyticsData = {
                  revenue: {
                    today: data.revenue?.today || 0,
                    thisWeek: data.revenue?.thisWeek || 0,
                    thisMonth: data.revenue?.thisMonth || 0,
                    total: data.revenue?.total || 0,
                    trend: data.revenue?.trend || 0,
                    errand_revenue: data.revenue?.errand_revenue || 0,
                    stall_revenue: data.revenue?.stall_revenue || 0
                  },
                  usage: {
                    totalBookings: data.usage?.totalBookings || 0,
                    activeBookings: data.usage?.activeBookings || 0,
                    completedBookings: data.usage?.completedBookings || 0,
                    cancelledBookings: data.usage?.cancelledBookings || 0,
                    utilizationRate: data.usage?.utilizationRate || 0
                  },
                  errands: {
                    total: data.errands?.total || 0,
                    active: data.errands?.active || 0,
                    completed: data.errands?.completed || 0,
                    cash_on_delivery: data.errands?.cash_on_delivery || 0
                  },
                  stalls: {
                    total_rentals: data.stalls?.total_rentals || 0,
                    active_rentals: data.stalls?.active_rentals || 0,
                    completed_rentals: data.stalls?.completed_rentals || 0,
                    containers: {
                      total: data.stalls?.containers?.total || 0,
                      active: data.stalls?.containers?.active || 0,
                      compartments: {
                        total: data.stalls?.containers?.compartments?.total || 0,
                        available: data.stalls?.containers?.compartments?.available || 0,
                        occupied: data.stalls?.containers?.compartments?.occupied || 0
                      }
                    }
                  },
                  vendors: {
                    total: data.vendors?.total || 0,
                    approved: data.vendors?.approved || 0,
                    pending: data.vendors?.pending || 0
                  },
                  users: {
                    total: data.users?.total || 0,
                    newThisMonth: data.users?.newThisMonth || 0,
                    activeUsers: data.users?.active || 0,
                    growthRate: data.users?.growthRate || 0
                  },
                  locations: {
                    total: data.locations?.total || 0,
                    activeLocations: data.locations?.activeLocations || 0,
                    totalLockers: data.locations?.totalLockers || 0,
                    availableLockers: data.locations?.availableLockers || 0
                  },
                  performance: {
                    averageBookingDuration: data.performance?.averageBookingDuration || 0,
                    peakHours: data.performance?.peakHours || [],
                    topLocations: data.performance?.topLocations || [],
                    customerRetentionRate: data.performance?.customerRetentionRate || 0
                  },
                  trends: {
                    revenueGrowth: data.trends?.revenueGrowth || 0,
                    userGrowth: data.trends?.userGrowth || 0,
                    bookingGrowth: data.trends?.bookingGrowth || 0,
                    period: selectedPeriod
                  },
                  pwa: data.pwa || undefined,
                  recentActivity: data.recentActivity || []
                };
                setAnalytics(mappedData);
              } else {
                console.error('Analytics API error:', response.status);
                toast.error('Failed to load analytics data');
                // Fallback to mock data
                setMockAnalytics();
              }
            } catch (error) {
              console.error('Analytics fetch error:', error);
              toast.error('Network error - using sample data');
              // Fallback to mock data on error
              setMockAnalytics();
            } finally {
              setIsLoading(false);
            }
          };

  const setMockAnalytics = () => {
    const mockData: AnalyticsData = {
      revenue: {
        today: 1250,
        thisWeek: 8750,
        thisMonth: 28750,
        total: 125000,
        trend: 12.5,
        errand_revenue: 4500,
        stall_revenue: 3200
      },
      usage: {
        totalBookings: 1247,
        activeBookings: 89,
        completedBookings: 1120,
        cancelledBookings: 38,
        utilizationRate: 78.5
      },
      errands: {
        total: 234,
        active: 45,
        completed: 189,
        cash_on_delivery: 67
      },
      stalls: {
        total_rentals: 156,
        active_rentals: 23,
        completed_rentals: 133,
        containers: {
          total: 12,
          active: 10,
          compartments: {
            total: 480,
            available: 156,
            occupied: 324
          }
        }
      },
      vendors: {
        total: 28,
        approved: 24,
        pending: 4
      },
      users: {
        total: 456,
        newThisMonth: 23,
        activeUsers: 234,
        growthRate: 8.2
      },
      locations: {
        total: 3,
        activeLocations: 3,
        totalLockers: 156,
        availableLockers: 67
      },
      performance: {
        averageBookingDuration: 4.2, // hours
        peakHours: ['10:00-12:00', '14:00-16:00', '18:00-20:00'],
        topLocations: [
          { location: 'Main Mall', bookings: 245 },
          { location: 'Riverwalk Mall', bookings: 189 },
          { location: 'Central Station', bookings: 156 }
        ],
        customerRetentionRate: 78.5
      },
      trends: {
        revenueGrowth: 12.5,
        userGrowth: 8.2,
        bookingGrowth: 15.3,
        period: 'month'
      },
      recentActivity: [
        {
          id: '1',
          type: 'booking',
          description: 'New booking at Main Mall - Locker A12',
          amount: 25,
          timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString()
        },
        {
          id: '2',
          type: 'user',
          description: 'New user registration: john.doe@email.com',
          timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString()
        },
        {
          id: '3',
          type: 'payment',
          description: 'Payment received for booking #1234',
          amount: 35,
          timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString()
        },
        {
          id: '4',
          type: 'booking',
          description: 'Booking completed at Riverwalk Mall - Locker B8',
          amount: 15,
          timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString()
        }
      ]
    };
    setAnalytics(mockData);
  };

  const formatCurrency = (amount: number) => {
    return `BWP ${amount.toLocaleString()}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
        );
      case 'user':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        );
      case 'payment':
        return (
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  if (user?.role !== 'admin') {
    return <div className="p-6">Access denied. Admin privileges required.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="p-6">Failed to load analytics data.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedPeriod('today')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              selectedPeriod === 'today'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setSelectedPeriod('week')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              selectedPeriod === 'week'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            This Week
          </button>
          <button
            onClick={() => setSelectedPeriod('month')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              selectedPeriod === 'month'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            This Month
          </button>
          <button
            onClick={() => setSelectedPeriod('year')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              selectedPeriod === 'year'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            This Year
          </button>
        </div>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Today's Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(analytics.revenue.today)}</p>
              <p className={`text-sm ${analytics.revenue.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analytics.revenue.trend >= 0 ? '+' : ''}{analytics.revenue.trend}% from yesterday
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(analytics.revenue.thisMonth)}</p>
              <p className="text-sm text-gray-500">Total revenue</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Errand Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(analytics.revenue.errand_revenue)}</p>
              <p className="text-sm text-gray-500">Delivery services</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Stall Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(analytics.revenue.stall_revenue)}</p>
              <p className="text-sm text-gray-500">Smart stall rentals</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Usage Statistics */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Usage Statistics</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Bookings</span>
                <span className="font-semibold text-gray-900">{analytics.usage.totalBookings.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed Bookings</span>
                <span className="font-semibold text-green-600">{analytics.usage.completedBookings.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Cancelled Bookings</span>
                <span className="font-semibold text-red-600">{analytics.usage.cancelledBookings.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Success Rate</span>
                <span className="font-semibold text-blue-600">
                  {formatPercentage((analytics.usage.completedBookings / analytics.usage.totalBookings) * 100)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Errand Statistics */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Errand Statistics</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Errands</span>
                <span className="font-semibold text-gray-900">{analytics.errands.total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Errands</span>
                <span className="font-semibold text-blue-600">{analytics.errands.active.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed Errands</span>
                <span className="font-semibold text-green-600">{analytics.errands.completed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Cash on Delivery</span>
                <span className="font-semibold text-purple-600">{analytics.errands.cash_on_delivery.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stall and Vendor Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Stall Statistics */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Smart Stall Statistics</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Rentals</span>
                <span className="font-semibold text-gray-900">{analytics.stalls.total_rentals.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Rentals</span>
                <span className="font-semibold text-blue-600">{analytics.stalls.active_rentals.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed Rentals</span>
                <span className="font-semibold text-green-600">{analytics.stalls.completed_rentals.toLocaleString()}</span>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Container Utilization</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Containers</span>
                    <span className="font-semibold text-gray-900">{analytics.stalls.containers.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Active Containers</span>
                    <span className="font-semibold text-blue-600">{analytics.stalls.containers.active}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Compartments</span>
                    <span className="font-semibold text-gray-900">{analytics.stalls.containers.compartments.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Available Compartments</span>
                    <span className="font-semibold text-green-600">{analytics.stalls.containers.compartments.available}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Occupied Compartments</span>
                    <span className="font-semibold text-red-600">{analytics.stalls.containers.compartments.occupied}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Statistics */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Vendor Statistics</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Vendors</span>
                <span className="font-semibold text-gray-900">{analytics.vendors.total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Approved Vendors</span>
                <span className="font-semibold text-green-600">{analytics.vendors.approved.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending Approval</span>
                <span className="font-semibold text-yellow-600">{analytics.vendors.pending.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Approval Rate</span>
                <span className="font-semibold text-blue-600">
                  {formatPercentage((analytics.vendors.approved / analytics.vendors.total) * 100)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Statistics */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">User Statistics</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{analytics.users.total.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-green-600">{analytics.users.newThisMonth.toLocaleString()}</p>
              <p className="text-sm text-gray-600">New This Month</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-blue-600">{analytics.users.activeUsers.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Active Users</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-purple-600">+{formatPercentage(analytics.users.growthRate)}</p>
              <p className="text-sm text-gray-600">Growth Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* PWA Install Statistics */}
      {analytics.pwa && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">PWA Install Statistics</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Total Installs</p>
                    <p className="text-2xl font-bold text-purple-900">{analytics.pwa.total_installs}</p>
                  </div>
                  <div className="text-3xl">📱</div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Stash-it Installs</p>
                    <p className="text-2xl font-bold text-blue-900">{analytics.pwa.stashit_installs}</p>
                  </div>
                  <div className="text-3xl">📦</div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Dash Installs</p>
                    <p className="text-2xl font-bold text-green-900">{analytics.pwa.dash_installs}</p>
                  </div>
                  <div className="text-3xl">⚡</div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">This Period</p>
                    <p className="text-2xl font-bold text-orange-900">{analytics.pwa.installs_this_period}</p>
                  </div>
                  <div className="text-3xl">📈</div>
                </div>
              </div>
            </div>

            {/* Platform Breakdown */}
            {Object.keys(analytics.pwa.installs_by_platform || {}).length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Installs by Platform</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(analytics.pwa.installs_by_platform).map(([platform, count]) => (
                    <div key={platform} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-600 capitalize">{platform}</p>
                      <p className="text-xl font-bold text-gray-900">{count as number}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Analytics Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Performance Metrics */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Performance Metrics</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Booking Duration</span>
                <span className="font-semibold text-blue-600">{analytics.performance.averageBookingDuration.toFixed(1)} hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Customer Retention</span>
                <span className="font-semibold text-green-600">{formatPercentage(analytics.performance.customerRetentionRate)}</span>
              </div>
              <div>
                <span className="text-gray-600 block mb-2">Peak Hours</span>
                <div className="flex flex-wrap gap-2">
                  {analytics.performance.peakHours.map((hour, index) => (
                    <span key={index} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                      {hour}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Trends */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Growth Trends ({analytics.trends.period})</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Revenue Growth</span>
                <span className={`font-semibold ${analytics.trends.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics.trends.revenueGrowth >= 0 ? '+' : ''}{formatPercentage(analytics.trends.revenueGrowth)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">User Growth</span>
                <span className={`font-semibold ${analytics.trends.userGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics.trends.userGrowth >= 0 ? '+' : ''}{formatPercentage(analytics.trends.userGrowth)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Booking Growth</span>
                <span className={`font-semibold ${analytics.trends.bookingGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics.trends.bookingGrowth >= 0 ? '+' : ''}{formatPercentage(analytics.trends.bookingGrowth)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Geographical Insights */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Top Locations by Bookings</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {analytics.performance.topLocations.map((location, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <span className="font-medium text-gray-900">{location.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{
                        width: `${(location.bookings / Math.max(...analytics.performance.topLocations.map(l => l.bookings))) * 100}%`
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-600 w-12 text-right">
                    {location.bookings}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {analytics.recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center space-x-4">
                {getActivityIcon(activity.type)}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500">{formatDate(activity.timestamp)}</p>
                </div>
                {activity.amount && (
                  <div className="text-sm font-semibold text-green-600">
                    {formatCurrency(activity.amount)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
